import { proxy, snapshot } from 'valtio';

import { publicConfig } from '@public-config';

import { CommentService } from '@diagram/services/CommentService';
import { derivedDiagramState } from '@diagram/state/diagram';

import { PermissionDiagramElements, PermissionDiagramGeneral, PermissionDigramComments, UserTip } from 'models';
import { TeamUserHelpers } from 'helpers/TeamUserHelpers';

import { ApiService } from 'services/ApiService';
import { state as currentProjectState } from 'state/current-project';
import { permissionsState } from 'state/current-project-permissions';
import { state as currentTeamState } from 'state/current-team';
import { state as currentUserState } from 'state/current-user';

export const tipState = proxy({
    tips: [] as UserTip[],
});

const TipService = {
    dismiss: async (tip: UserTip) => {
        await ApiService.post(`${publicConfig.urls.api}/users/me/tips/${tip}/dismiss`);
    },

    seen: (tip: UserTip) => {
        const { user } = currentUserState;

        if (!user) {
            return true;
        }

        return user?.dismissedTips?.includes(tip);
    },

    hasTipPermission: (tip: UserTip) => {
        const permissions = snapshot(permissionsState).permissions;

        switch (tip) {
            case UserTip.RESIZE_COMPONENT:
                return permissions[PermissionDiagramElements.EDIT] ?? false;
            case UserTip.INVITE_COLLABORATOR:
                return permissions[PermissionDiagramGeneral.SHARE] ?? false;
            case UserTip.ADD_COMMENT:
                return permissions[PermissionDigramComments.CREATE] ?? false;
            case UserTip.LINK_PRODUCT:
                return permissions[PermissionDiagramElements.EDIT] ?? false;
            case UserTip.DOWNLOAD_IMAGE:
                return permissions[PermissionDiagramElements.CREATE] ?? false;
            default:
                return false;
        }
    },

    filter: (tips: UserTip[]) => {
        return tips.filter((tip) => {
            return TipService.hasTipPermission(tip) && !TipService.seen(tip);
        });
    },

    autotrigger: () => {
        const { project } = currentProjectState;
        const { team } = currentTeamState;

        if (project?.permissionsAs !== 'user') {
            return;
        }

        const nbComponents = derivedDiagramState.componentInstanceKeys.length;
        const nbComments = CommentService.getCachedComments().length ?? 0;

        const nbCollaborators = project?.collaborators.users.docs.length ?? 0;
        const nbTeamUsers = TeamUserHelpers.fromTeam(team).length;
        const nbSharedUsers = nbCollaborators + nbTeamUsers;

        const hasLinkedProducts = derivedDiagramState.componentInstancesAsArray.some(
            (component) => component.componentId,
        );

        const showInviteCollaborator = nbComponents >= 5 && nbSharedUsers === 0;
        const showComments = nbSharedUsers > 1 && nbComments === 0;
        const showLinkProduct = nbComponents >= 5 && !hasLinkedProducts;
        const showDownloadImage = nbComponents >= 10;

        if (showInviteCollaborator) {
            TipService.trigger(UserTip.INVITE_COLLABORATOR);
        }

        if (showComments) {
            TipService.trigger(UserTip.ADD_COMMENT);
        }

        if (showLinkProduct) {
            TipService.trigger(UserTip.LINK_PRODUCT);
        }

        if (showDownloadImage) {
            TipService.trigger(UserTip.DOWNLOAD_IMAGE);
        }
    },

    getAll: () => {
        const tips: UserTip[] = [];

        const addTip = (tip: UserTip) => {
            if (TipService.hasTipPermission(tip)) {
                tips.push(tip);
            }
        };

        Object.values(UserTip).forEach((tip) => {
            addTip(tip);
        });

        return tips;
    },

    trigger: (tip: UserTip) => {
        if (!tipState.tips.includes(tip)) {
            tipState.tips.push(tip);
        }
    },
};

export { TipService };
